import { useFormContext } from "../../contexts/form-context";
import { useFieldInput } from "../../modules/field-set";
import { RegisterOptions } from "../../shared/types";
import { handleErrorMessage } from "../../shared/utils";
import { KeyboardEvent, ReactNode, Ref, useCallback } from "react";

export type FieldProps<TBaseComponentProps> = Omit<TBaseComponentProps, "name" | "registerOptions"> & {
    name: string,
    registerOptions?: RegisterOptions
}

export interface RegisterFieldProps extends RegisterOptions {
    name: string,
    disabled?: boolean,
    children: (props: {
        ref: Ref<any>,
        value: any,
        disabled: boolean,
        handleChange: (value: any) => void,
        handleBlur: () => void,
        handleKeyDown: (e: KeyboardEvent<any>) => void,
        hasError: boolean,
        errorMessage: string,
    }) => ReactNode
}

export default function RegisterField({
    name,
    disabled,
    children,
    ...registerOptions
}: RegisterFieldProps) {
    const form = useFormContext();

    const { focus, enterBehaviour: receivedEnterBehaviour, ...formFieldOptions } = { ...registerOptions };
    const enterBehaviour = receivedEnterBehaviour || form.enterBehaviour;

    const fieldSetField = useFieldInput({ name, focus });

    const handleEnterBehaviour = useCallback((e: KeyboardEvent) => {
        if (e.defaultPrevented) return;
        if (e.key !== "Enter" || e.ctrlKey) return;
        if (enterBehaviour === "none") return;

        e.preventDefault();
        e.stopPropagation();

        switch (enterBehaviour) {
            case "submit":
                form.fieldSet.clickSubmitButton();
                return;
            case "focusNext":
                if (e.shiftKey) {
                    form.fieldSet.focusPreviousField(fieldSetField.id);
                } else {
                    form.fieldSet.focusNextField(fieldSetField.id);
                }
                return;
            case "selectNext":
                if (e.shiftKey) {
                    form.fieldSet.focusPreviousField(fieldSetField.id, true);
                } else {
                    form.fieldSet.focusNextField(fieldSetField.id, true);
                }
                return;
        }
    }, [enterBehaviour, form]);

    return <form.Field name={name} {...formFieldOptions}>
        {formField => {
            const [hasError, errorMessage] = formField.state.meta.isTouched
                ? handleErrorMessage(formField.state.meta.errors)
                : [false, ""];

            const handleChange = (value: any) => {
                formField.handleChange(value);
                if (formField.state.meta.errorMap.onSubmit) {
                    formField.setMeta(prev => ({ ...prev, errorMap: { ...prev.errorMap, onSubmit: undefined } }));
                }
                if ((form.state.errorMap as any).onSubmit) {
                    form.setErrorMap({ onSubmit: undefined } as any);
                }
            };

            return children({
                ref: fieldSetField.ref,
                value: formField.state.value,
                disabled: Boolean(form.disabled || disabled),
                handleChange,
                handleBlur: formField.handleBlur,
                handleKeyDown: handleEnterBehaviour,
                hasError: hasError,
                errorMessage: errorMessage
            })
        }}
    </form.Field>
}